import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { hasSeenTourVersion } from '@shared/setup'
import { useAuth } from '../../contexts/auth-context'
import { useVoiceCallSession } from '../../hooks/use-voice-call-session'
import { isTerminalCallPhase } from '../../stores/voice-call-store'
import { useIsAppLocked } from '../../stores/app-lock-store'
import { useSessionFollowerStatus } from '../../hooks/use-sessions-nav-visible'
import { updateMySetupState } from '../../services/appsync-client'
import { givesConsentForAnyone, useSessionFollowing } from '../../stores/session-following-store'
import { patchSetupProfile, useSetupProfile } from '../../stores/setup-profile-store'
import { isIndependentUser } from '../../utils/rbac'
import {
  isCaptureLive,
  useActiveSessionRecording,
} from '../session-recording/hooks/use-active-session-recording'
import { usePremiumSessionRecording } from '../session-recording/hooks/use-premium-session-recording'
import { requestSetup, useSetupPending } from '../setup/data/setup-gate'
import { OnboardingTour } from './onboarding-tour'
import { markTutorialComplete, shouldShowTutorial, TUTORIAL_VERSION } from './tour-version'
import {
  hasBlockingModal,
  isTourAutoStartBlocked,
  ONBOARDING_TOUR_ATTR,
} from './tour-start-gate'

type TourContextValue = {
  /** Force-start the walkthrough (manual "Replay walkthrough"). */
  startTour: () => void
  /** Open setup on demand (Menu → Account → Replay set up). */
  openSetup: () => void
}

const TourContext = createContext<TourContextValue | null>(null)

/** Routes where the tour must never appear (unauthenticated / pre-app). */
const BLOCKED_PREFIXES = ['/sign-in', '/auth', '/privacy', '/terms', '/delete-account']

/** Home base where every walkthrough anchor is guaranteed to be rendered. */
const TOUR_HOME = '/messages/direct'

/**
 * First-run order: setup (features/setup, its own host) → this walkthrough.
 * The walkthrough auto-starts only once setup has finished, been put off, or
 * isn't owed this launch (`setupPending`), and only when this person hasn't
 * seen the current version on any device (`setupState.tourVersionSeen`,
 * localStorage as the cache).
 */
type TourPhase = 'idle' | 'steps'

export function OnboardingTourProvider({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [phase, setPhase] = useState<TourPhase>('idle')
  const [blockingModalOpen, setBlockingModalOpen] = useState(false)
  const callSession = useVoiceCallSession()
  const recording = useActiveSessionRecording()
  const softLocked = useIsAppLocked()
  const followerStatus = useSessionFollowerStatus()
  const premiumSessionRecording = usePremiumSessionRecording()
  const setupPending = useSetupPending()
  const userId = user?.userId ?? null
  const setupProfile = useSetupProfile(userId)
  const following = useSessionFollowing(user && isIndependentUser(user) ? userId : null)
  const consentGiver = givesConsentForAnyone(following)

  const onBlockedRoute = BLOCKED_PREFIXES.some((p) => location.pathname.startsWith(p))

  // A voice/video/group call (incoming, outgoing, connecting, or active) owns
  // the screen via its own full-screen overlay. The tour must never paint over
  // it, so we suppress auto-start and hide any in-progress tour while a call is
  // present, then let it resume once the call ends.
  const callActive =
    !!callSession &&
    !isTerminalCallPhase(callSession.phase) &&
    callSession.phase !== 'elsewhere'

  // A session recording in progress owns the screen too (REC bar, stop, pause):
  // the tour waits for it to finish, exactly like a call.
  const recordingActive = recording.ref !== null || isCaptureLive(recording.snapshot)

  const autoStartBlocked = isTourAutoStartBlocked({
    isAuthLoading: isLoading,
    hasUser: Boolean(user),
    onBlockedRoute,
    callActive,
    recordingActive,
    softLocked,
    blockingModalOpen,
    followSummaryPending: followerStatus === 'pending',
    setupPending,
  })
  const autoStartBlockedRef = useRef(autoStartBlocked)
  autoStartBlockedRef.current = autoStartBlocked

  const serverSetupState = setupProfile.status === 'loaded' ? setupProfile.profile?.setupState : undefined
  const seenOnAnotherDevice = hasSeenTourVersion(serverSetupState, TUTORIAL_VERSION)

  // Seen elsewhere: update this device's cache so it never replays.
  useEffect(() => {
    if (seenOnAnotherDevice && shouldShowTutorial()) markTutorialComplete()
  }, [seenOnAnotherDevice])

  const owed = useCallback(
    () => shouldShowTutorial() && !hasSeenTourVersion(serverSetupState, TUTORIAL_VERSION),
    [serverSetupState],
  )

  const close = useCallback(() => {
    markTutorialComplete()
    setPhase('idle')
    if (!userId) return
    void updateMySetupState({ tourVersionSeen: TUTORIAL_VERSION })
      .then((res) => patchSetupProfile(userId, { setupState: res.setupState }))
      .catch(() => {
        /* the local cache still stops a replay on this device */
      })
  }, [userId])

  const startTour = useCallback(() => {
    // Ensure the anchored controls exist before the overlay measures them.
    if (location.pathname !== TOUR_HOME) navigate(TOUR_HOME)
    setPhase('steps')
  }, [location.pathname, navigate])

  const openSetup = useCallback(() => requestSetup({ mode: 'manual' }), [])

  // Watch for MFA / setup / other modal dialogs that aren't React state the
  // tour already knows about. When they close, `blockingModalOpen` flips and
  // the auto-start effect re-arms.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const sync = () => {
      const next = hasBlockingModal(document)
      setBlockingModalOpen((prev) => (prev === next ? prev : next))
    }
    sync()
    const observer = new MutationObserver(sync)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['role', 'aria-modal', ONBOARDING_TOUR_ATTR],
    })
    return () => observer.disconnect()
  }, [])

  // Auto-start after setup until completed. Defer (don't consume completion)
  // while auth, setup, a call, soft-lock/MFA, or another modal is in the way —
  // retry as soon as the gate clears.
  useEffect(() => {
    if (phase !== 'idle' || autoStartBlocked) return
    if (setupProfile.status === 'loading' || setupProfile.status === 'idle') return
    if (!owed()) return
    const id = window.setTimeout(() => {
      if (autoStartBlockedRef.current || !owed()) return
      setPhase('steps')
    }, 400)
    return () => window.clearTimeout(id)
  }, [phase, autoStartBlocked, owed, setupProfile.status])

  const visible = phase !== 'idle' && user && !onBlockedRoute && !callActive && !recordingActive && !softLocked

  return (
    <TourContext.Provider value={{ startTour, openSetup }}>
      {children}
      {visible && phase === 'steps' && (
        <OnboardingTour
          onClose={close}
          premiumSessionRecording={premiumSessionRecording}
          consentGiver={consentGiver}
        />
      )}
    </TourContext.Provider>
  )
}

/** Access the walkthrough controls (e.g. to replay it on demand). */
export function useOnboardingTour(): TourContextValue {
  const ctx = useContext(TourContext)
  if (!ctx) {
    // Defensive no-op so a misplaced consumer never crashes the app.
    return { startTour: () => {}, openSetup: () => {} }
  }
  return ctx
}
